import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../helpers/firebase";
import { getAuth } from "firebase/auth";
import { getOrCreateNotificationSettings } from "./notificationSettingsModel";

/**
 * Reminder data model
 */
export const Reminder = {
  id: "",
  ownerId: "",
  tenantId: "",
  unitId: "",
  type: "rent_reminder", // 'rent_reminder', 'late_fee', 'lease_renewal'
  status: "pending", // 'pending', 'sent', 'failed', 'cancelled'
  channel: "email", // 'email', 'sms', 'both'
  scheduledDate: null,
  sentDate: null,
  message: "",
  subject: "",
  recipientEmail: "",
  recipientPhone: "",
  retryCount: 0,
  maxRetries: 3,
  createdAt: null,
  updatedAt: null,
};

/**
 * Late fee rule data model
 */
export const LateFeeRule = {
  id: "",
  ownerId: "",
  gracePeriod: 3, // days after due date
  flatFee: 50, // flat late fee amount
  percentageFee: 5, // percentage of rent
  maxLateFee: 200, // maximum late fee cap
  compounding: false, // whether late fees compound
  autoApply: true, // automatically apply late fees
  createdAt: null,
  updatedAt: null,
};

/**
 * Create a new reminder
 */
export async function createReminder(reminderData) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Get notification settings to populate templates
    const notificationSettings = await getOrCreateNotificationSettings();

    // Determine subject and message based on type
    let subject = reminderData.subject;
    let message = reminderData.message;

    if (reminderData.type === "rent_reminder" && !subject) {
      subject =
        notificationSettings.emailTemplates?.rentReminder?.subject ||
        "Rent Reminder - Payment Due Soon";
      message =
        notificationSettings.emailTemplates?.rentReminder?.body ||
        "Hi {{tenantName}},\n\nThis is a friendly reminder that your rent payment of ${{amount}} is due on {{dueDate}}.\n\nThank you!";
    } else if (reminderData.type === "late_fee" && !subject) {
      subject =
        notificationSettings.emailTemplates?.lateFeeNotice?.subject ||
        "Late Fee Applied - Overdue Rent";
      message =
        notificationSettings.emailTemplates?.lateFeeNotice?.body ||
        "Hi {{tenantName}},\n\nA late fee of ${{lateFeeAmount}} has been applied to your account for the overdue rent payment.\n\nThank you!";
    }

    const reminder = {
      ...reminderData,
      ownerId: user.uid,
      status: "pending",
      retryCount: 0,
      subject: subject,
      message: message,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, "reminders"), reminder);

    return {
      success: true,
      reminder: { id: docRef.id, ...reminder },
      message: "Reminder created successfully",
    };
  } catch (error) {
    console.error("Error creating reminder:", error);
    throw error;
  }
}

/**
 * Get pending reminders for a specific date
 */
export async function getPendingReminders(date) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    const q = query(
      collection(db, "reminders"),
      where("ownerId", "==", user.uid),
      where("status", "==", "pending"),
      where("scheduledDate", ">=", startOfDay),
      where("scheduledDate", "<=", endOfDay),
      orderBy("scheduledDate", "asc")
    );

    const querySnapshot = await getDocs(q);
    const reminders = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return reminders;
  } catch (error) {
    console.error("Error getting pending reminders:", error);
    throw error;
  }
}

/**
 * Mark reminder as sent
 */
export async function markReminderSent(reminderId) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const reminderRef = doc(db, "reminders", reminderId);
    await updateDoc(reminderRef, {
      status: "sent",
      sentDate: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return {
      success: true,
      message: "Reminder marked as sent",
    };
  } catch (error) {
    console.error("Error marking reminder as sent:", error);
    throw error;
  }
}

/**
 * Get late fee rules for an owner
 */
export async function getLateFeeRules() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const q = query(
      collection(db, "lateFeeRules"),
      where("ownerId", "==", user.uid)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      // Return default rules if none exist
      return {
        gracePeriod: 3,
        flatFee: 50,
        percentageFee: 5,
        maxLateFee: 200,
        compounding: false,
        autoApply: true,
      };
    }

    const doc = querySnapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (error) {
    console.error("Error getting late fee rules:", error);
    throw error;
  }
}

/**
 * Update late fee rules
 */
export async function updateLateFeeRules(rules) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const q = query(
      collection(db, "lateFeeRules"),
      where("ownerId", "==", user.uid)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      // Create new rules
      const newRules = {
        ...rules,
        ownerId: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, "lateFeeRules"), newRules);
      return { id: docRef.id, ...newRules };
    } else {
      // Update existing rules
      const doc = querySnapshot.docs[0];
      const docRef = doc(db, "lateFeeRules", doc.id);

      await updateDoc(docRef, {
        ...rules,
        updatedAt: serverTimestamp(),
      });

      return { id: doc.id, ...doc.data(), ...rules };
    }
  } catch (error) {
    console.error("Error updating late fee rules:", error);
    throw error;
  }
}

/**
 * Calculate late fee for a payment
 */
export async function calculateLateFee(paymentAmount, daysLate, rules = null) {
  try {
    if (!rules) {
      rules = await getLateFeeRules();
    }

    if (daysLate <= rules.gracePeriod) {
      return 0;
    }

    let lateFee = rules.flatFee;

    if (rules.percentageFee > 0) {
      const percentageAmount = (paymentAmount * rules.percentageFee) / 100;
      lateFee = Math.max(lateFee, percentageAmount);
    }

    if (rules.maxLateFee > 0) {
      lateFee = Math.min(lateFee, rules.maxLateFee);
    }

    return Math.round(lateFee * 100) / 100; // Round to 2 decimal places
  } catch (error) {
    console.error("Error calculating late fee:", error);
    throw error;
  }
}

/**
 * Apply late fee to a payment
 */
export async function applyLateFee(paymentId, lateFeeAmount) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // TODO: Update payment record with late fee
    // This would typically update the payment document in Firestore

    // Create a late fee record
    const lateFeeRecord = {
      paymentId,
      ownerId: user.uid,
      amount: lateFeeAmount,
      appliedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    };

    await addDoc(collection(db, "lateFees"), lateFeeRecord);

    return {
      success: true,
      lateFee: lateFeeRecord,
      message: "Late fee applied successfully",
    };
  } catch (error) {
    console.error("Error applying late fee:", error);
    throw error;
  }
}

/**
 * Generate reminders for all tenants based on their settings
 */
export async function generateTenantReminders() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Get all active tenants
    const tenantsRef = collection(db, "tenants");
    const q = query(
      tenantsRef,
      where("ownerId", "==", user.uid),
      where("status", "==", "active")
    );

    const querySnapshot = await getDocs(q);
    const tenants = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    const today = new Date();
    const reminders = [];

    for (const tenant of tenants) {
      // Generate rent reminders based on tenant settings
      if (
        tenant.rentReminderSettings &&
        tenant.rentReminderSettings.length > 0
      ) {
        for (const reminderSetting of tenant.rentReminderSettings) {
          if (!reminderSetting.enabled) continue;

          // Calculate when this reminder should be sent
          const rentDueDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            tenant.rentDueDay || 1
          );
          if (rentDueDate < today) {
            rentDueDate.setMonth(rentDueDate.getMonth() + 1);
          }

          const reminderDate = new Date(rentDueDate);
          reminderDate.setDate(
            reminderDate.getDate() - reminderSetting.daysBeforeDue
          );

          // If reminder should be sent today or in the future
          if (reminderDate >= today) {
            const reminder = {
              ownerId: user.uid,
              tenantId: tenant.id,
              unitId: tenant.unitId,
              type: "rent_reminder",
              status: "pending",
              channel: reminderSetting.channel,
              scheduledDate: reminderDate,
              recipientEmail: tenant.email,
              recipientPhone: tenant.phone,
              subject: "", // Will be populated by createReminder
              message: "", // Will be populated by createReminder
              retryCount: 0,
              maxRetries: 3,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            };

            reminders.push(reminder);
          }
        }
      }

      // Generate late notifications based on tenant settings
      if (
        tenant.lateRentNotificationSettings &&
        tenant.lateRentNotificationSettings.length > 0
      ) {
        for (const lateSetting of tenant.lateRentNotificationSettings) {
          if (!lateSetting.enabled) continue;

          // Calculate when this notification should be sent
          const rentDueDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            tenant.rentDueDay || 1
          );
          if (rentDueDate < today) {
            rentDueDate.setMonth(rentDueDate.getMonth() + 1);
          }

          const notificationDate = new Date(rentDueDate);
          notificationDate.setDate(
            notificationDate.getDate() + lateSetting.daysAfterDue
          );

          // If notification should be sent today or in the future
          if (notificationDate >= today) {
            const reminder = {
              ownerId: user.uid,
              tenantId: tenant.id,
              unitId: tenant.unitId,
              type: "late_fee",
              status: "pending",
              channel: lateSetting.channel,
              scheduledDate: notificationDate,
              recipientEmail: tenant.email,
              recipientPhone: tenant.phone,
              subject: "", // Will be populated by createReminder
              message: "", // Will be populated by createReminder
              retryCount: 0,
              maxRetries: 3,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            };

            reminders.push(reminder);
          }
        }
      }
    }

    // Create all reminders
    const createdReminders = [];
    for (const reminder of reminders) {
      const result = await createReminder(reminder);
      if (result.success) {
        createdReminders.push(result.reminder);
      }
    }

    return {
      success: true,
      reminders: createdReminders,
      message: `Generated ${createdReminders.length} reminders`,
    };
  } catch (error) {
    console.error("Error generating tenant reminders:", error);
    throw error;
  }
}
